import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { PhoneInput } from "@/components/customer/PhoneInput";
import { Input } from "@/components/ui/Input";
import { X } from "lucide-react";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const AuthModal = ({ isOpen, onClose, onSuccess }: AuthModalProps) => {
  const { checkDevice, sendCode, verifyCode } = useAuth();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [codeId, setCodeId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      // Check if device is known
      const isKnown = await checkDevice(phoneNumber);

      if (isKnown) {
        // Device recognized, skip SMS
        onSuccess();
        handleClose();
      } else {
        // Send SMS code
        const newCodeId = await sendCode(phoneNumber);
        setCodeId(newCodeId);
        setStep("code");
      }
    } catch (err: unknown) {
      const errorMessage =
        err && typeof err === "object" && "response" in err
          ? (err.response as { data?: { message?: string } })?.data?.message
          : undefined;
      setError(errorMessage || "Failed to send code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      await verifyCode(codeId, verificationCode, name);
      onSuccess();
      handleClose();
    } catch (err: unknown) {
      const errorMessage =
        err && typeof err === "object" && "response" in err
          ? (err.response as { data?: { message?: string } })?.data?.message
          : undefined;
      setError(errorMessage || "Invalid code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Reset state when closing
    setStep("phone");
    setPhoneNumber("");
    setVerificationCode("");
    setCodeId("");
    setName("");
    setError("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md z-10 animate-fade-in-up">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute -top-12 right-0 text-white/60 hover:text-white transition-colors p-2"
          aria-label="Close modal"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Logo/Header */}
        <div className="text-center mb-6">
          <h1 className="text-4xl font-black text-white uppercase tracking-wider mb-3">
            Peter's <span className="text-gradient-primary">Cuts</span>
          </h1>
          <div className="mx-auto w-40 h-1 bg-gradient-primary mb-4" />
          <p className="text-text-tertiary uppercase tracking-widest text-xs font-bold">
            Get Cut. No Wait.
          </p>
        </div>

        {/* Auth Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-center">
              {step === "phone" ? "Enter Your Phone" : "Verify Code"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {step === "phone" ? (
              <form onSubmit={handlePhoneSubmit} className="space-y-6">
                <PhoneInput
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  error={error}
                />
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  isLoading={isLoading}
                  disabled={!phoneNumber}
                  size="lg"
                >
                  Continue
                </Button>
              </form>
            ) : (
              <form onSubmit={handleCodeSubmit} className="space-y-6">
                <div className="text-center mb-4 p-4 bg-bg-card-alt rounded-lg border border-accent-cyan/30">
                  <p className="text-sm text-text-tertiary uppercase tracking-wider mb-2 font-bold">
                    Code sent to
                  </p>
                  <p className="text-lg text-accent-cyan font-bold">
                    {phoneNumber}
                  </p>
                </div>

                <Input
                  type="text"
                  label="Verification Code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="123456"
                  maxLength={6}
                  error={error}
                />

                <Input
                  type="text"
                  label="Your Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  maxLength={100}
                  required
                />

                <div className="space-y-3">
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-full"
                    isLoading={isLoading}
                    disabled={verificationCode.length !== 6 || !name.trim()}
                    size="lg"
                  >
                    Verify & Continue
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => setStep("phone")}
                  >
                    Change Phone Number
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center mt-6 text-text-muted text-xs uppercase tracking-widest">
          Powered by SMS Authentication
        </p>
      </div>
    </div>
  );
};
